import dayjs from 'dayjs'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material'

type Course = {
  id: string
  name: string
  displayName?: string
  section?: string
  length: number
}

type User = {
  id: string
  firstName: string
  lastName: string
  primaryEmail: string
  licenseTypes?: string[]
}

type SummaryModalProps = {
  isOpen: boolean
  onClose: () => void
  type: 'users' | 'courses'
  users?: User[]
  courses?: Course[]
  selectedUserIDs: string[]
  selectedCourseIDs: string[]
}

export default function SummaryModal({
  isOpen,
  onClose,
  type,
  users = [],
  courses = [],
  selectedUserIDs,
  selectedCourseIDs,
}: SummaryModalProps) {
  let selectedUsers = users.filter((user) => selectedUserIDs.includes(user.id))
  let selectedCourses = courses.filter((course) =>
    selectedCourseIDs.includes(course.id),
  )

  let handleExportCSV = () => {
    let data =
      type === 'users'
        ? selectedUsers.map((user) => {
            let assignedCourses = selectedCourses
              .map((course) => course.name)
              .join(', ')
            return {
              'First Name': user.firstName,
              'Last Name': user.lastName,
              Email: user.primaryEmail,
              'License Types': user.licenseTypes?.join(', ') || '',
              'Assigned Courses': assignedCourses,
            }
          })
        : selectedCourses.map((course) => ({
            'Course Name': course.name,
            'Display Name': course.displayName || '',
            Section: course.section || '',
            'Length (Hours)': course.length,
          }))

    if (data.length === 0) return

    let csv = [
      Object.keys(data[0]).join(','),
      ...data.map((row) =>
        Object.values(row)
          .map((val) =>
            typeof val === 'string' && val.includes(',') ? `"${val}"` : val,
          )
          .join(','),
      ),
    ]

    let blob = new Blob([csv.join('\n')], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    // eslint-disable-next-line fp/no-mutation
    a.href = url
    // eslint-disable-next-line fp/no-mutation
    a.download = `assignment-${type}-${dayjs().format('YYYY-MM-DD')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle className="flex items-center justify-between">
        <Typography variant="h6">
          {type === 'users' ? 'Selected Users' : 'Selected Courses'}
        </Typography>
        <Box className="flex items-center gap-2">
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className="p-0">
        {type === 'courses' ? (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Course Name</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell align="right">Length (Hours)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCourses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>{course.displayName || '-'}</TableCell>
                    <TableCell>{course.section || 'No Section'}</TableCell>
                    <TableCell align="right">{course.length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" className="mb-4 font-bold">
                Assignment will include {selectedCourses.length} course(s):
              </Typography>
              <Box className="flex flex-wrap gap-2">
                {selectedCourses.slice(0, 5).map((course) => (
                  <Chip
                    key={course.id}
                    size="small"
                    label={course.name}
                    variant="outlined"
                  />
                ))}
                {selectedCourses.length > 5 && (
                  <Chip
                    size="small"
                    label={`+${selectedCourses.length - 5} more`}
                    variant="outlined"
                    color="primary"
                  />
                )}
              </Box>
            </Box>

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>License Types</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell>{user.primaryEmail}</TableCell>
                      <TableCell>
                        {user.licenseTypes?.length ? (
                          <Box className="flex flex-wrap gap-1">
                            {user.licenseTypes.map((license) => (
                              <Chip
                                key={license}
                                size="small"
                                label={license}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
